import React, { useState, useEffect } from 'react';
import DoctorNavbar from './DocNav';
import { useNavigate } from 'react-router-dom';
import { FaFileUpload, FaSpinner, FaTimes, FaExclamationTriangle, FaNotesMedical, FaPills, FaUserInjured, FaCalendarAlt } from 'react-icons/fa';

const MedicalRecordForm = ({ 
  initialData = {}, 
  onSubmit, 
  isEditMode = false,
  backUrl = '/dashboard'
}) => {
  const [formData, setFormData] = useState({
    patientId: initialData.patientId || '',
    recordType: initialData.recordType || 'consultation',
    title: initialData.title || '',
    date: initialData.date || new Date().toISOString().split('T')[0],
    symptoms: initialData.symptoms || '',
    diagnosis: initialData.diagnosis || '',
    prescription: initialData.prescription || '',
    notes: initialData.notes || '',
  });
  
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();
  
  // Validation logic
  useEffect(() => {
    const validateField = (name, value) => {
      if (!touchedFields[name]) return null;
      
      switch (name) {
        case 'patientId':
          return !value ? 'Patient ID is required' : null;
        case 'title':
          return !value ? 'Title is required' : null;
        case 'date':
          return !value ? 'Date is required' : null;
        case 'recordType':
          return !value ? 'Record type is required' : null;
        default:
          return null;
      }
    };
    
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    });
    
    setErrors(newErrors);
  }, [formData, touchedFields]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };
  
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields(prev => ({ ...prev, [name]: true }));
  };
  
  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };
  
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const newTouchedFields = {};
    Object.keys(formData).forEach(key => {
      newTouchedFields[key] = true;
    });
    setTouchedFields(newTouchedFields);
    
    // Check for errors
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        submitData.append(key, value);
      });
      
      files.forEach(file => {
        submitData.append('files', file);
      });
      
      await onSubmit(submitData);
      navigate(backUrl);
    } catch (err) {
      setSubmitError(err.response?.data?.message || 'Failed to save record');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const isFieldInvalid = (fieldName) => {
    return touchedFields[fieldName] && errors[fieldName];
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <DoctorNavbar />
      <div className="max-w-6xl mx-auto px-4 py-32 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-4">
            <h2 className="text-2xl font-bold text-white flex items-center">
              <FaNotesMedical className="mr-3" />
              {isEditMode ? 'Update Medical Record' : 'Add New Medical Record'}
            </h2>
            <p className="text-blue-100 mt-1">
              Fill in the details below to {isEditMode ? 'update the' : 'create a new'} medical record
            </p>
          </div>
          
          {/* Form Content */}
          <div className="p-6 md:p-8">
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-md">
                <div className="flex items-center">
                  <FaExclamationTriangle className="text-red-500 mr-3" />
                  <p className="text-red-700 font-medium">{submitError}</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              {/* Basic Information Section */}
              <section className="mb-8">
                <h3 className="text-lg font-semibold text-gray-700 pb-2 border-b border-gray-200 mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="patientId" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <FaUserInjured className="mr-2 text-blue-500" />
                      Patient ID*
                    </label>
                    <input
                      type="text"
                      id="patientId"
                      name="patientId"
                      value={formData.patientId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:outline-none 
                        ${isFieldInvalid('patientId') 
                          ? 'border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:ring-blue-200'}`}
                    />
                    {isFieldInvalid('patientId') && (
                      <p className="mt-1 text-sm text-red-600">{errors.patientId}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="date" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <FaCalendarAlt className="mr-2 text-blue-500" />
                      Date*
                    </label>
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:outline-none 
                        ${isFieldInvalid('date') 
                          ? 'border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:ring-blue-200'}`}
                    />
                    {isFieldInvalid('date') && (
                      <p className="mt-1 text-sm text-red-600">{errors.date}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="recordType" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <FaNotesMedical className="mr-2 text-blue-500" />
                      Record Type*
                    </label>
                    <select
                      id="recordType"
                      name="recordType"
                      value={formData.recordType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:outline-none 
                        ${isFieldInvalid('recordType') 
                          ? 'border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:ring-blue-200'}`}
                    >
                      <option value="consultation">Consultation</option>
                      <option value="lab_result">Lab Result</option>
                      <option value="imaging">Imaging</option>
                      <option value="prescription">Prescription</option>
                      <option value="procedure">Procedure</option>
                    </select>
                    {isFieldInvalid('recordType') && (
                      <p className="mt-1 text-sm text-red-600">{errors.recordType}</p>
                    )}
                  </div>
                  
                  <div className="md:col-span-2">
                    <label htmlFor="title" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Title*
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g., Annual Checkup, Blood Test Results, etc."
                      className={`w-full p-3 border rounded-lg transition-colors focus:ring-2 focus:outline-none 
                        ${isFieldInvalid('title') 
                          ? 'border-red-500 focus:ring-red-200' 
                          : 'border-gray-300 focus:ring-blue-200'}`}
                    />
                    {isFieldInvalid('title') && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                  </div>
                </div>
              </section>
              
              {/* Medical Details Section */}
              <section className="mb-8">
                <h3 className="text-lg font-semibold text-gray-700 pb-2 border-b border-gray-200 mb-4">
                  Medical Details
                </h3>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="symptoms" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Patient Symptoms
                    </label>
                    <textarea
                      id="symptoms"
                      name="symptoms"
                      value={formData.symptoms}
                      onChange={handleChange}
                      placeholder="Describe patient's reported symptoms"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none"
                    ></textarea>
                  </div>
                  
                  <div>
                    <label htmlFor="diagnosis" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Diagnosis
                    </label>
                    <textarea
                      id="diagnosis"
                      name="diagnosis"
                      value={formData.diagnosis}
                      onChange={handleChange}
                      placeholder="Enter diagnosis details"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none"
                    ></textarea>
                  </div>
                  
                  <div>
                    <label htmlFor="prescription" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      <FaPills className="mr-2 text-blue-500" />
                      Prescription
                    </label>
                    <textarea
                      id="prescription"
                      name="prescription"
                      value={formData.prescription}
                      onChange={handleChange}
                      placeholder="Enter medication details, dosage, and instructions"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none"
                    ></textarea>
                  </div>
                  
                  <div>
                    <label htmlFor="notes" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any additional information that may be relevant"
                      rows={3}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-200 focus:outline-none"
                    ></textarea>
                  </div>
                </div>
              </section>
              
              {/* File Attachments Section */}
              <section className="mb-8">
                <h3 className="text-lg font-semibold text-gray-700 pb-2 border-b border-gray-200 mb-4">
                  Attachments
                </h3>
                <div>
                  <div className="mt-2 flex justify-center px-6 py-6 border-2 border-gray-300 border-dashed rounded-lg 
                    hover:bg-gray-50 transition-colors">
                    <div className="space-y-2 text-center">
                      <FaFileUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm justify-center">
                        <label htmlFor="file-upload" className="relative cursor-pointer rounded-md font-medium text-blue-600 hover:text-blue-500 
                          focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <span>Upload files</span>
                          <input 
                            id="file-upload" 
                            name="file-upload" 
                            type="file" 
                            multiple 
                            className="sr-only" 
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1 text-gray-600">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, PDF up to 10MB each
                      </p>
                    </div>
                  </div>
                  
                  {/* File Preview */}
                  {files.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Attached Files:</h4>
                      <div className="flex flex-wrap gap-2">
                        {files.map((file, index) => (
                          <div key={index} className="relative bg-gray-50 px-3 py-2 rounded-md border border-gray-300 
                            hover:bg-gray-100 transition-colors">
                            <div className="flex items-center">
                              <span className="text-sm truncate max-w-xs mr-2">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeFile(index)}
                                className="text-gray-400 hover:text-red-500 focus:outline-none"
                                aria-label="Remove file"
                              >
                                <FaTimes />
                              </button>
                            </div>
                            <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>
              
              {/* Form Actions */}
              <div className="pt-6 border-t border-gray-200 flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => navigate(backUrl)}
                  className="py-2 px-6 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 
                    bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || Object.keys(errors).length > 0}
                  className={`py-2 px-6 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                    bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                    ${(isSubmitting || Object.keys(errors).length > 0) 
                      ? 'opacity-70 cursor-not-allowed' 
                      : 'hover:bg-blue-700 transition-colors'}`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <FaSpinner className="animate-spin mr-2" />
                      {isEditMode ? 'Updating...' : 'Saving...'}
                    </span>
                  ) : (
                    isEditMode ? 'Update Record' : 'Save Record'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalRecordForm;